import { Component, OnInit } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { CreateScheduleModalComponent } from '../create-schedule-modal/create-schedule-modal.component';
import { QuerySchedulers, SchedulerStatus } from '../../../../API';
import { SchedulerRecord, SchedulingService } from '../../services/scheduling-service';
import { Status } from '../../models/scheduling-model';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'app-scheduling',
  templateUrl: './scheduling.component.html',
  styleUrl: './scheduling.component.scss',
})
export class SchedulingComponent implements OnInit {
  headers: any[] = [];
  rows: SchedulerRecord[] = [];
  filteredData: SchedulerRecord[] = [];
  searchInput?: string;
  listOfData: readonly QuerySchedulers[] = [];
  listOfCurrentPageData: readonly QuerySchedulers[] = [];
  isLoading?: boolean;
  orgId = '';
  expandSet = new Set<string>();
  isDeleteModalVisible = false;
  schedulerName = '';
  selectedSchedulerRecord!: SchedulerRecord;
  scheduleOnlyDeleteLoading = false;
  schedulerAndQueryDeleteLoading = false;

  constructor(
    private modalService: NzModalService,
    private schedulingService: SchedulingService,
    private messageService: NzMessageService,
  ) {
    this.setHeaders();
  }

  async ngOnInit(): Promise<void> {
    await this.loadSchedulerList();
  }

  private async loadSchedulerList(): Promise<void> {
    this.isLoading = true;
    try {
      const [schedulerRows] = await Promise.all([this.schedulingService.getSchedulerList()]);

      this.rows = schedulerRows;
      this.filteredData = [...this.rows];
    } catch (error) {
      console.error('Failed to load scheduler data', error);
    } finally {
      this.isLoading = false;
    }
  }

  search(): void {
    this.filteredData = this.rows.filter((row) => this.matchesSearch(row));
  }

  openCreateScheduleModal(): void {
    this.modalService.create({
      nzTitle: 'Create New Schedule',
      nzFooter: null,
      nzContent: CreateScheduleModalComponent,
      nzMaskClosable: false,
      nzWidth: '90vw',
      nzOnOk: () => {
        this.loadSchedulerList();
      },
    });
  }

  setHeaders(): void {
    this.headers = [
      {
        name: 'Scheduler ID',
        width: '13.75rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) => (a.schedulerId ?? '').localeCompare(b.schedulerId ?? ''),
        sortDirections: ['ascend', 'descend', null],
        sortOrder: null,
      },
      {
        name: 'Scheduler Name',
        width: '17.5rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) => a.name.localeCompare(b.name),
        sortDirections: ['ascend', 'descend', null],
        sortOrder: null,
      },
      {
        name: 'Query',
        width: '13.75rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) => (a.queryName ?? '').localeCompare(b.queryName ?? ''),
        sortDirections: ['ascend', 'descend', null],
        sortOrder: null,
      },
      {
        name: 'Next Run on',
        width: '13.25rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) => {
          return new Date(a.nextRunOn).getTime() - new Date(b.nextRunOn).getTime();
        },
        sortDirections: ['ascend', 'descend', null],
        sortOrder: null,
      },
      {
        name: 'Scheduler Ends on',
        width: '12.5rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) =>
          new Date(a.scheduleEndsOn).getTime() - new Date(b.scheduleEndsOn).getTime(),
        sortDirections: ['ascend', 'descend', null],
        sortOrder: null,
      },
      {
        name: 'Execution Type',
        width: '8rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) => (a.type ?? '').localeCompare(b.type ?? ''),
        sortDirections: ['ascend', 'descend', null],
        sortOrder: null,
      },
      {
        name: 'Created By',
        width: '12.5rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) => (a.createdBy ?? '').localeCompare(b.createdBy ?? ''),
        sortDirections: ['ascend', 'descend', null],
        sortOrder: null,
      },
      {
        name: 'Created On',
        width: '12.5rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        sortDirections: ['ascend', 'descend', null],
        sortOrder: 'descend',
      },
      {
        name: 'Status',
        width: '8.75rem',
        sortFn: (a: SchedulerRecord, b: SchedulerRecord) =>
          (a.schedulerStatus ?? '').localeCompare(b.schedulerStatus ?? ''),
        sortDirections: ['ascend', 'descend', null],
        sortOrder: null,
      },
      {
        name: 'Action',
        width: '7rem',
        right: '0',
        sortOrder: null,
        sortFn: null,
        sortDirections: [],
      },
    ];
  }
  trackByHeader(index: number, item: { name: string }): string {
    return item.name;
  }
  onExpandChange(id: string, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }

  getGridColumn(columnIndex: number): number {
    return columnIndex + 2;
  }

  formatDate(date: Date): string {
    return date == null || undefined
      ? ''
      : new Date(date).toLocaleString(undefined, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });
  }

  getStatusColor(status: Status | undefined): string {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Paused':
        return 'warning';
      case 'Expired':
        return 'error';
      default:
        return 'default';
    }
  }

  private matchesSearch(row: SchedulerRecord): boolean {
    const searchValue = this.searchInput?.trim().toLowerCase();

    if (!searchValue) {
      return true;
    }

    return [
      row.queryName,
      row.name,
      row.nextRunOn,
      row.scheduleEndsOn,
      row.type,
      row.createdBy,
      row.createdAt,
      row.schedulerStatus,
      row.nextRunOnSuffix,
    ].some((value) => value?.toLowerCase().includes(searchValue));
  }

  showDeleteScheduleModal(record: SchedulerRecord) {
    this.isDeleteModalVisible = true;
    this.populateSelectedSchedulerRecord(record);
  }

  populateSelectedSchedulerRecord(record: SchedulerRecord) {
    if (record !== null || record !== undefined) {
      this.selectedSchedulerRecord = record;
    }
  }

  async deleteSchedule(deletionType: string): Promise<void> {
    const schedulerId = this.selectedSchedulerRecord?.schedulerId;
    const queryId = this.selectedSchedulerRecord?.queryId;

    if (schedulerId == '' || schedulerId == null || schedulerId == undefined) {
      this.messageService.error('Unable to delete schedule.');
      return;
    }

    if (queryId == '' || queryId == null || queryId == undefined) {
      this.messageService.error('Unable to delete schedule. Query information not found.');
      return;
    }

    if (deletionType === 'SCHEDULER_AND_QUERY' && this.isQueryAssociatedWithMultipleSchedulers()) {
      this.showMultipleSchedulersWarning();
      return;
    }

    const isSchedulerAndQueryDeletion = deletionType === 'SCHEDULER_AND_QUERY';
    this.scheduleOnlyDeleteLoading = !isSchedulerAndQueryDeletion;
    this.schedulerAndQueryDeleteLoading = isSchedulerAndQueryDeletion;
    try {
      const response = await this.schedulingService.deleteSchedule(schedulerId, deletionType);
      if (response?.success && response?.successCode == 200) {
        this.messageService.success(response?.message ?? 'Schedule deleted successfully.');
      }
    } catch (error) {
      console.error(error);
      this.messageService.error('Failed to delete schedule.');
    } finally {
      this.scheduleOnlyDeleteLoading = false;
      this.schedulerAndQueryDeleteLoading = false;
      this.isDeleteModalVisible = false;
      this.loadSchedulerList();
    }
  }

  private isQueryAssociatedWithMultipleSchedulers(): boolean {
    return this.getAssociatedSchedulers().length > 1;
  }

  private getAssociatedSchedulers(): SchedulerRecord[] {
    const queryId = this.selectedSchedulerRecord?.queryId?.trim();

    if (!queryId) {
      return [];
    }

    return this.rows.filter((row) => row.queryId?.trim() === queryId);
  }

  private showMultipleSchedulersWarning(): void {
    const associatedSchedulers = this.getAssociatedSchedulers();
    const schedulerNames = associatedSchedulers
      .map((scheduler) => scheduler.name)
      .filter((name) => name)
      .join('<br>• ');
    const schedulerCount = associatedSchedulers.length;

    this.modalService.confirm({
      nzTitle: 'Cannot Delete Query',
      nzContent: `This query is currently associated with ${schedulerCount} scheduler(s) and cannot be deleted. You can still delete only this scheduler, or remove the query from all associated schedulers before deleting the query.<br><br><strong>Associated Schedulers:</strong><br>• ${schedulerNames || '--'}`,
      nzOkText: 'Ok',
      nzCancelText: null,
    });
  }

  closeScheduleModal() {
    this.isDeleteModalVisible = false;
  }

  async updateSchedule(row: SchedulerRecord): Promise<void> {
    this.isLoading = true;
    if (row == null || row == undefined) {
      this.messageService.error('Unable to update schedule. Scheduler information not found.');
      return;
    }
    const schedulerId = row?.schedulerId;
    const status = row?.schedulerStatus;
    const name = row?.name;
    const scheduleStatus = this.mapSchedulerStatus(status).toLowerCase();
    try {
      const response = await this.schedulingService.updateSchedule(schedulerId, this.mapSchedulerStatus(status));
      if (response) {
        this.messageService.success(`Schedule: <b>${name}</b> is now ${scheduleStatus}.`);
      }
    } catch (error) {
      console.error(error);
      this.messageService.error('Failed to update schedule.');
    } finally {
      this.isLoading = false;
      this.loadSchedulerList();
    }
  }

  mapSchedulerStatus(status: string): SchedulerStatus {
    switch (status?.toLocaleLowerCase()) {
      case 'active':
        return SchedulerStatus.PAUSED;
      case 'paused':
        return SchedulerStatus.ACTIVE;
      default:
        return SchedulerStatus.EXPIRED;
    }
  }
}
